import { v4 as uuidv4 } from "uuid";
import { uploadFile } from "../../../services/filesAPI";
import { RFI_TAG_HEIGHT, CONNECTION_INNER_RECT_INSET } from "../constants";

/**
 * Display URL for a linked photo: prefer uploaded asset URL; fall back to legacy inline data URL.
 */
export function getLinkedPhotoDisplayUrl(annotation) {
  if (!annotation) return null;
  if (annotation.linkedPhotoUrl) return annotation.linkedPhotoUrl;
  if (annotation.linkedPhotoPreviewUrl) return annotation.linkedPhotoPreviewUrl;
  const lp = annotation.linkedPhoto;
  if (typeof lp === "string" && lp.startsWith("data:")) return lp;
  return null;
}

export function annotationHasLinkedPhoto(annotation) {
  return !!(annotation?.photoTag && getLinkedPhotoDisplayUrl(annotation));
}

/**
 * Upload a single image for a drawing annotation; returns stable URL + filename for persistence.
 */
export async function uploadAnnotationPhoto(projectId, file, onProgress) {
  const formData = new FormData();
  formData.append("files", file);
  const data = await uploadFile(projectId, formData, onProgress || (() => {}));
  const f = data.files?.[0];
  if (!f?.url) {
    throw new Error(data.message || "Photo upload failed");
  }
  return {
    url: f.url,
    filename: f.filename || file.name,
  };
}

/**
 * Before saving: upload pending `File` (from blob preview), or legacy data URLs, then persist `linkedPhotoUrl` only.
 * @param {Map<string, File>|undefined} connectionFiles - annotation id → File for unsaved blob previews
 */
export async function normalizeConnectionPhotosForPersist(annotation, projectId, connectionFiles) {
  if (!annotation || annotation.type !== "connection") return annotation;

  if (annotation.linkedPhotoUrl) {
    const { linkedPhoto, linkedPhotoPreviewUrl, linkedRfiId, linkedRfiTagId, rfiTag, ...rest } =
      annotation;
    return {
      ...rest,
      linkedRfiId: null,
      linkedRfiTagId: null,
      rfiTag: null,
    };
  }

  const pendingFile = connectionFiles?.get?.(annotation.id);
  if (pendingFile instanceof File) {
    const { url, filename } = await uploadAnnotationPhoto(projectId, pendingFile, () => {});
    connectionFiles?.delete?.(annotation.id);
    const { linkedPhoto, linkedPhotoPreviewUrl, ...rest } = annotation;
    return { ...rest, linkedPhotoUrl: url, linkedPhotoName: filename };
  }

  const lp = annotation.linkedPhoto;
  if (typeof lp === "string" && lp.startsWith("data:")) {
    const blob = await fetch(lp).then((r) => r.blob());
    const ext = (blob.type && blob.type.split("/")[1]) || "jpg";
    const file = new File([blob], annotation.linkedPhotoName || `annotation-photo.${ext}`, {
      type: blob.type || "image/jpeg",
    });
    const { url, filename } = await uploadAnnotationPhoto(projectId, file, () => {});
    const { linkedPhoto: _drop, linkedPhotoPreviewUrl: _pv, ...rest } = annotation;
    return { ...rest, linkedPhotoUrl: url, linkedPhotoName: filename };
  }

  if (annotation.linkedPhotoPreviewUrl) {
    throw new Error(
      "Could not upload linked photo. Try removing and re-attaching the image, then save again."
    );
  }

  return annotation;
}

export function buildPhotoLinkFields(boxStart, boxEnd, pageIdx, connectionId, photoRef) {
  const boxX = Math.min(boxStart.x, boxEnd.x);
  const boxY = Math.min(boxStart.y, boxEnd.y);
  const boxW = Math.abs(boxEnd.x - boxStart.x);
  const boxH = Math.abs(boxEnd.y - boxStart.y);
  const innerRectInset = CONNECTION_INNER_RECT_INSET;
  const tagHeight = RFI_TAG_HEIGHT;
  const connPt = {
    x: boxX + boxW - innerRectInset,
    y: boxY + boxH - innerRectInset,
  };
  const linkedPhotoTagId = uuidv4();
  const photoTag = {
    id: linkedPhotoTagId,
    type: "photo",
    connectionId,
    connectionPoint: connPt,
    tagPosition: {
      x: connPt.x,
      y: boxY + boxH - innerRectInset - tagHeight / 2,
    },
    page: pageIdx,
  };

  // Blob preview + File for multipart upload on Save (no huge data URL in state)
  if (
    photoRef &&
    typeof photoRef === "object" &&
    photoRef.file instanceof File &&
    typeof photoRef.objectUrl === "string" &&
    photoRef.objectUrl.length > 0
  ) {
    return {
      linkedPhotoUrl: null,
      linkedPhoto: undefined,
      linkedPhotoPreviewUrl: photoRef.objectUrl,
      linkedPhotoName: photoRef.filename || photoRef.file.name || "photo.jpg",
      linkedPhotoTagId,
      photoTag,
    };
  }

  // Defer S3 upload until Save: keep only inline data URL (normalizeConnectionPhotosForPersist uploads on save)
  if (
    photoRef &&
    typeof photoRef === "object" &&
    photoRef.linkedPhoto &&
    typeof photoRef.linkedPhoto === "string" &&
    photoRef.linkedPhoto.startsWith("data:")
  ) {
    return {
      linkedPhotoUrl: null,
      linkedPhoto: photoRef.linkedPhoto,
      linkedPhotoName: photoRef.filename || photoRef.linkedPhotoName || "photo.jpg",
      linkedPhotoTagId,
      photoTag,
    };
  }

  const url = typeof photoRef === "string" ? photoRef : photoRef.url;
  const filename = typeof photoRef === "object" && photoRef && photoRef.filename ? photoRef.filename : undefined;

  return {
    linkedPhotoUrl: url,
    ...(filename ? { linkedPhotoName: filename } : {}),
    linkedPhotoTagId,
    photoTag,
  };
}
